/* Collection tile caption contrast (card-product.liquid / component-card.css).

   Liquid classifies portrait featured images as `card-wrapper--tall`
   (full-bleed cover; overridable per image via "#cover"/"#no-cover" alt
   tags). The caption sits over the image on tall tiles and, on desktop
   hover, over the secondary image on every tile — either can be dark.

   Once an image loads, sample the strip behind the caption on a canvas:
   - featured image dark  -> `caption-contrast-light` (white text + scrim)
   - secondary image      -> `caption-hover-light` / `caption-hover-dark`
     so the caption crossfades with the hover image (CSS transitions).

   Runs again automatically when swatch interactions swap the image sources.

   The canvas gets a 40px CDN rendition of the image, fetched as soon as the
   image file is chosen and decoded off the main thread (createImageBitmap on
   a Blob). Drawing the displayed file itself (535px on phones) decoded it on
   the main thread, 30-60 ms per card at 4x CPU, while people scroll and tap.
   Each verdict is kept in localStorage, so a repeat view does no work. Over
   314 card images the 40px rendition gave the 535px verdict for all but one
   borderline image; the 360px desktop rendition already disagreed with the
   535px one on two. */
(() => {
  const FIRST = '.collection-grid-section .card .media > img:first-child';
  const SECONDARY = '.collection-grid-section .card .media > img.card__media-secondary';
  const SAMPLE_WIDTH = 40;
  const processed = new WeakMap(); // img -> last sampled src

  const analyzeStrip = (data) => {
    let sum = 0;
    let count = 0;
    let dark = 0;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 200) continue; // skip transparent pixels
      const lum = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
      sum += lum;
      if (lum < 0.6) dark++; // too dark for dark caption text to read against
      count++;
    }
    if (!count) return { mean: 1, darkFraction: 0 };
    return { mean: sum / count, darkFraction: dark / count };
  };

  // A single average misclassifies mixed backgrounds (e.g. a dark coat beside
  // white bedding): dark caption text then lands unreadable over the dark part.
  // White text + scrim is readable over ANY backdrop, so bias toward it — use
  // dark text only when the strip is almost entirely light.
  const needsLightCaption = (s) => s.darkFraction > 0.18 || s.mean < 0.62;

  let canvas = null;
  const stripStats = (bitmap) => {
    const w = SAMPLE_WIDTH;
    const h = Math.max(8, Math.round(w * (bitmap.height / bitmap.width)));
    canvas = canvas || document.createElement('canvas');
    canvas.width = w; // also clears what the previous sample drew
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(bitmap, 0, 0, w, h);
    const stripTop = Math.round(h * 0.82);
    return analyzeStrip(ctx.getImageData(0, stripTop, w, h - stripTop).data);
  };

  // Verdict cache: image file (any rendition width) -> 1 light / 0 dark caption.
  // Shopify file URLs carry a version (?v=), so an entry never goes stale.
  // Bump the key's suffix whenever the sampling or thresholds change.
  const STORE_KEY = 'aster:caption-tone:v1';
  const STORE_LIMIT = 500;
  let tones = null;
  let unsaved = false;
  let storageWorks = true;

  const readTones = () => {
    if (tones) return tones;
    tones = new Map();
    try {
      const saved = JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
      Object.keys(saved).forEach((key) => tones.set(key, saved[key]));
    } catch (error) {
      console.warn('card-tile-intel: caption cache unreadable, sampling every image', error);
    }
    return tones;
  };

  const saveTones = () => {
    if (!unsaved || !storageWorks) return;
    unsaved = false;
    while (tones.size > STORE_LIMIT) tones.delete(tones.keys().next().value); // oldest first
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(tones)));
    } catch (error) {
      storageWorks = false;
      console.warn('card-tile-intel: caption cache not saved', error);
    }
  };

  const idle = window.requestIdleCallback
    ? (fn) => window.requestIdleCallback(fn, { timeout: 2000 })
    : (fn) => setTimeout(fn, 1000);

  const rememberTone = (key, light) => {
    tones.set(key, light ? 1 : 0);
    if (unsaved) return;
    unsaved = true;
    idle(saveTones);
  };

  window.addEventListener('pagehide', saveTones);

  const sampleTone = (url) => fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.blob();
    })
    .then((blob) => createImageBitmap(blob))
    .then((bitmap) => {
      try {
        return needsLightCaption(stripStats(bitmap));
      } finally {
        bitmap.close();
      }
    });

  const inflight = new Map(); // key -> sample in progress

  // Caption verdict for an image URL: true = light caption. A boolean when
  // already known, else a promise of one (null when the sample failed).
  const requestTone = (src) => {
    const url = new URL(src, window.location.href);
    url.searchParams.delete('width');
    const key = url.pathname.replace(/^\/cdn\/shop\//, '') + url.search;
    const known = readTones().get(key);
    if (known !== undefined) return known === 1;
    if (!inflight.has(key)) {
      url.searchParams.set('width', String(SAMPLE_WIDTH));
      // one task per sample: the cards that load together must not add up
      // to one long task
      const job = new Promise((resolve) => { setTimeout(resolve, 0); })
        .then(() => sampleTone(url.href))
        .then((light) => {
          rememberTone(key, light);
          return light;
        }, (error) => {
          console.warn('card-tile-intel: could not sample, keeping the current contrast', url.href, error);
          return null;
        })
        .then((light) => {
          inflight.delete(key);
          return light;
        });
      inflight.set(key, job);
    }
    return inflight.get(key);
  };

  const withCaptionTone = (src, apply) => {
    const tone = requestTone(src);
    if (typeof tone === 'boolean') {
      apply(tone);
    } else {
      tone.then((light) => { if (light !== null) apply(light); });
    }
  };

  // The 40px request takes a round trip, so start it as soon as the image
  // file is chosen rather than when the card nears the viewport: the verdict
  // is then usually ready by the time the classes are applied.
  const warm = (img) => {
    if (img.currentSrc) requestTone(img.currentSrc);
  };

  const analyze = (img) => {
    const wrapper = img.closest('.card-wrapper');
    const src = img.currentSrc || img.src;
    if (!wrapper || !src || processed.get(img) === src) return;
    processed.set(img, src);
    // a swatch swap can replace the image while its sample is in flight
    const stillShown = () => processed.get(img) === src;

    if (img.matches(SECONDARY)) {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        const ratio = img.naturalWidth / img.naturalHeight;
        wrapper.classList.toggle('card-wrapper--hover-tall', ratio < 0.95 || ratio > 1.05);
      }
      withCaptionTone(src, (light) => {
        if (!stillShown()) return;
        wrapper.classList.toggle('caption-hover-light', light);
        wrapper.classList.toggle('caption-hover-dark', !light);
      });
      return;
    }

    // Classify by the CURRENTLY displayed first image's pixel ratio — a
    // swatch swap can replace a portrait lifestyle with a square packshot (or
    // vice versa), so the tall/cover decision cannot be a one-time Liquid
    // call. Square (≈1:1) sits contained; any rectangle covers the tile.
    // `data-card-cover` ("force"/"never", set from the alt tag) overrides.
    const override = wrapper.dataset.cardCover;
    let isTall;
    if (override === 'force') {
      isTall = true;
    } else if (override === 'never') {
      isTall = false;
    } else if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      const ratio = img.naturalWidth / img.naturalHeight;
      isTall = ratio < 0.95 || ratio > 1.05;
    } else {
      isTall = wrapper.classList.contains('card-wrapper--tall');
    }
    wrapper.classList.toggle('card-wrapper--tall', isTall);

    if (!isTall) {
      wrapper.classList.remove('caption-contrast-light');
      return;
    }
    withCaptionTone(src, (light) => {
      if (stillShown()) wrapper.classList.toggle('caption-contrast-light', light);
    });
  };

  const visibilityObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        const img = entry.target;
        if (img.complete && img.naturalWidth > 0) analyze(img);
      });
    }, { rootMargin: '200px' })
    : null;

  const scheduleAnalysis = (img) => {
    if (visibilityObserver) visibilityObserver.observe(img);
    else analyze(img);
  };

  const sweep = (scope = document) => {
    const selector = scope === document
      ? FIRST + ', ' + SECONDARY
      : '.card .media > img:first-child, .card .media > img.card__media-secondary';

    scope.querySelectorAll(selector).forEach((img) => {
      warm(img); // loaded or still downloading
      if (img.complete && img.naturalWidth > 0) scheduleAnalysis(img);
    });
  };

  // images finishing to load (lazy loading, swatch swaps) — load doesn't
  // bubble, so listen in the capture phase
  document.addEventListener('load', (event) => {
    const el = event.target;
    if (el instanceof HTMLImageElement && (el.matches(FIRST) || el.matches(SECONDARY))) {
      warm(el);
      scheduleAnalysis(el);
    }
  }, { capture: true, passive: true });

  document.addEventListener('card:media-changed', (event) => {
    const wrapper = event.target.closest('.card-wrapper');
    if (!wrapper) return;
    requestAnimationFrame(() => sweep(wrapper));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => sweep());
  } else {
    sweep();
  }
})();
